/**
 * Redact known secret patterns in free text: recorded command lines, command
 * output and JSON log payloads (ZTA design evaluation F13, decision D4 = A).
 *
 * Display-level only, like lib/mask.ts: the API still returns the raw text,
 * so this keeps credentials off screens, screenshots and copied snippets,
 * not out of the browser. Patterns are deliberately conservative: a false
 * positive hides a harmless value, a false negative shows a credential.
 */
export const REDACTED = '••••••';

const RULES: ReadonlyArray<[RegExp, string]> = [
  // PEM private keys and certificates' private parts, whole block.
  [/-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g, `-----BEGIN PRIVATE KEY----- ${REDACTED} -----END PRIVATE KEY-----`],
  // Authorization headers and bearer tokens.
  [/\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi, `$1 ${REDACTED}`],
  // JWTs anywhere.
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, REDACTED],
  // AWS access key ids and secret keys.
  [/\b(AKIA|ASIA)[A-Z0-9]{16}\b/g, REDACTED],
  [/(aws_secret_access_key\s*[=:]\s*)\S+/gi, `$1${REDACTED}`],
  // mysql/psql style -p<password> and --password=…
  [/(\s-p)(?!\s)(\S+)/g, `$1${REDACTED}`],
  [/(--password[=\s])\S+/gi, `$1${REDACTED}`],
  // key=value / key: value / "key": "value" for secret-sounding keys.
  [/("?(?:[A-Za-z0-9_-]*?)(?:password|passwd|pwd|secret|token|api[_-]?key|access[_-]?key|private[_-]?key|credentials?|auth)"?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,;}&]+)/gi, `$1"${REDACTED}"`],
  // Credentials in URLs: scheme://user:pass@host
  [/(\b[a-z][a-z0-9+.-]*:\/\/[^\s:/@]+:)[^\s@/]+@/gi, `$1${REDACTED}@`],
];

/** The text with every known secret pattern replaced by REDACTED. */
export function redactSecrets(text: string | null | undefined): string {
  let out = text ?? '';
  for (const [re, rep] of RULES) out = out.replace(re, rep);
  return out;
}

/** JSON-stringify a payload for display, redacting secret-sounding keys and values. */
export function redactJson(value: unknown): string {
  return redactSecrets(JSON.stringify(value, null, 2));
}
